import type { LeDataChatMessage, LeDataContentBlock, LeDataSessionVisibility } from '~/types/leData';

import { parseLeDataSessionResponse } from '~/utils/mapLeDataSessionToMessages';

import { textBlockMarkdown } from '../chat/answerMarkdown';

import type { AskUIMessage, AskUIMessagePart } from './askUiMessage';
import { imagePartOf } from './imageParts';

export interface SessionHistory {
  sessionId: string;
  messages: Array<AskUIMessage>;
  visibility: LeDataSessionVisibility;
  readOnly: boolean;
  /** Characters in the session's last model request; absent until a turn has recorded one. */
  contextChars?: number;
  /** session.createdAt and session.updatedAt, as the server sent them. */
  startedAt?: string;
  updatedAt?: string;
}

export class UnreadableHistoryError extends Error {
  constructor() {
    super('This conversation could not be loaded in this view.');
    this.name = 'UnreadableHistoryError';
  }
}

function isUiMessage(value: unknown): value is AskUIMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<AskUIMessage>;
  return (
    typeof candidate.id === 'string' &&
    (candidate.role === 'user' || candidate.role === 'assistant') &&
    Array.isArray(candidate.parts)
  );
}

function partOfBlock(block: LeDataContentBlock): AskUIMessagePart {
  if (block.type !== 'text') {
    return { type: `data-${block.type}`, id: block.id, data: block.payload } as AskUIMessagePart;
  }
  return block.payload.section === 'thought-process'
    ? { type: 'reasoning', text: textBlockMarkdown(block), state: 'done' }
    : { type: 'text', text: textBlockMarkdown(block), state: 'done' };
}

function uiMessageOfChatMessage(message: LeDataChatMessage): AskUIMessage {
  if (message.role === 'user') {
    return {
      id: message.id,
      role: 'user',
      parts: [...(message.images ?? []).map(imagePartOf), { type: 'text', text: message.text ?? '' }],
    };
  }
  return {
    id: message.id,
    role: 'assistant',
    metadata: { feedbackRating: message.feedbackRating },
    parts: (message.blocks ?? []).map(partOfBlock),
  };
}

function mergeAdjacentAssistants(messages: ReadonlyArray<AskUIMessage>): Array<AskUIMessage> {
  return messages.reduce<Array<AskUIMessage>>((merged, message) => {
    const previous = merged[merged.length - 1];
    if (previous?.role === 'assistant' && message.role === 'assistant') {
      merged[merged.length - 1] = { ...previous, parts: [...previous.parts, ...message.parts] };
    } else {
      merged.push(message);
    }
    return merged;
  }, []);
}

function withSessionId(messages: ReadonlyArray<AskUIMessage>, sessionId: string): Array<AskUIMessage> {
  return messages.map((message) =>
    message.role === 'assistant' && !message.metadata?.sessionId
      ? { ...message, metadata: { ...message.metadata, sessionId } }
      : message,
  );
}

export function sessionHistoryFromBody(body: unknown, sessionId: string): SessionHistory {
  const { messages, session, readOnly } = (body ?? {}) as {
    messages?: unknown;
    session?: { visibility?: unknown; contextChars?: unknown; createdAt?: unknown; updatedAt?: unknown };
    readOnly?: unknown;
  };
  const visibility = session?.visibility === 'luxuryescapes_link' ? 'luxuryescapes_link' : 'private';
  if (Array.isArray(messages) && messages.every(isUiMessage)) {
    return {
      sessionId,
      visibility,
      readOnly: readOnly === true,
      messages: withSessionId(messages, sessionId),
      ...(typeof session?.contextChars === 'number' ? { contextChars: session.contextChars } : {}),
      ...(typeof session?.createdAt === 'string' ? { startedAt: session.createdAt } : {}),
      ...(typeof session?.updatedAt === 'string' ? { updatedAt: session.updatedAt } : {}),
    };
  }
  if (readOnly === true) {
    const retired = mergeAdjacentAssistants(parseLeDataSessionResponse(body).messages.map(uiMessageOfChatMessage));
    return { sessionId, visibility, readOnly: true, messages: withSessionId(retired, sessionId) };
  }
  throw new UnreadableHistoryError();
}
